// The Vue build version to load with the `import` command
// (runtime-only or standalone) has been set in webpack.base.conf with an alias.
import Vue from 'vue';
import SuiVue from 'semantic-ui-vue';
import VeeValidate from 'vee-validate';
import VuejsDialog from 'vuejs-dialog';
import 'vuejs-dialog/dist/vuejs-dialog.min.css';
import App from '@/App';
import router from '@/router';
import store from '@/store';
import moment from 'moment';
import $axios, {
  createUserManager,
  isTokenExpired,
  attemptSilentRefresh,
} from '@/helpers/axios-instance';
import '../node_modules/semantic-ui-css/semantic.min.css';
import '../node_modules/vue-it-bigger/dist/vue-it-bigger.min.css';

Vue.config.productionTip = false;
// Vue.use(require('vue-moment'));

Vue.filter('moment', (value, format) => {
  if (value === null || value === undefined || format === undefined) {
    return '';
  }
  if (format === 'from') {
    return moment(value).fromNow();
  }
  return moment(value).format(format);
});

const userKey = `oidc.user:${process.env.OIDC_AUTHORITY}:${process.env.OIDC_CLIENT_ID}`;

Vue.use(SuiVue);
Vue.use(VeeValidate);
Vue.use(VuejsDialog);

// Flag to prevent multiple simultaneous authentication attempts
let isRefreshing = false;
let refreshPromise = null;

// Request interceptor - check token validity before making request
$axios.interceptors.request.use(
  async (config) => {
    // this will change based on the url + client name
    if (localStorage.getItem(userKey)) {
      const user = JSON.parse(localStorage.getItem(userKey));

      // Check if token is expired or about to expire
      if (isTokenExpired(user)) {
        // If already refreshing, wait for that promise
        if (isRefreshing && refreshPromise) {
          console.log('Token refresh already in progress, waiting...');
          try {
            await refreshPromise;
            // After refresh completes, get the new token
            const refreshedUser = JSON.parse(localStorage.getItem(userKey));
            if (refreshedUser && refreshedUser.access_token) {
              config.headers.Authorization = `Bearer ${refreshedUser.access_token}`;
            }
            return config;
          } catch (err) {
            return Promise.reject(err);
          }
        }

        // Start a new refresh attempt
        if (!isRefreshing) {
          isRefreshing = true;
          console.warn(
            'Access token has expired. Attempting silent renewal...',
          );

          const mgr = createUserManager();

          // Create a promise for the refresh operation
          refreshPromise = attemptSilentRefresh(mgr)
            .then((refreshedUser) => {
              if (refreshedUser) {
                // Silent refresh succeeded
                console.log(
                  'Token refreshed successfully, continuing with request',
                );
                isRefreshing = false;
                refreshPromise = null;
                return refreshedUser;
              } else {
                // Silent refresh failed, need full redirect
                console.warn(
                  'Silent renewal failed. Redirecting to authentication...',
                );
                return mgr
                  .signinRedirect({ state: window.location.href })
                  .then(() => {
                    throw new Error('Redirecting to authentication');
                  });
              }
            })
            .catch((err) => {
              isRefreshing = false;
              refreshPromise = null;
              throw err;
            });

          try {
            const refreshedUser = await refreshPromise;
            // Use the new token
            config.headers.Authorization = `Bearer ${refreshedUser.access_token}`;
            return config;
          } catch (err) {
            // Either silent refresh failed and we're redirecting, or there was an error
            return Promise.reject(err);
          }
        }
      }

      const token = user.access_token;
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// Response interceptor - handle 401 errors
$axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Check if error is 401 and we haven't already tried to refresh
    if (
      error.response &&
      error.response.status === 401 &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true;

      // If already refreshing, wait for that promise
      if (isRefreshing && refreshPromise) {
        console.log('Token refresh already in progress due to 401, waiting...');
        try {
          await refreshPromise;
          // Retry the original request with new token
          const refreshedUser = JSON.parse(localStorage.getItem(userKey));
          if (refreshedUser && refreshedUser.access_token) {
            originalRequest.headers.Authorization = `Bearer ${refreshedUser.access_token}`;
            return $axios(originalRequest);
          }
        } catch (err) {
          return Promise.reject(error);
        }
      }

      if (!isRefreshing) {
        isRefreshing = true;
        console.warn('Received 401 Unauthorized. Attempting silent renewal...');

        const mgr = createUserManager();

        // Create a promise for the refresh operation
        refreshPromise = attemptSilentRefresh(mgr)
          .then((refreshedUser) => {
            if (refreshedUser) {
              // Silent refresh succeeded
              console.log(
                'Token refreshed successfully after 401, retrying request',
              );
              isRefreshing = false;
              refreshPromise = null;
              return refreshedUser;
            } else {
              // Silent refresh failed, need full redirect
              console.warn(
                'Silent renewal failed after 401. Redirecting to authentication...',
              );
              return mgr
                .signinRedirect({ state: window.location.href })
                .then(() => {
                  throw new Error('Redirecting to authentication');
                });
            }
          })
          .catch((err) => {
            isRefreshing = false;
            refreshPromise = null;
            throw err;
          });

        try {
          const refreshedUser = await refreshPromise;
          // Retry the original request with new token
          originalRequest.headers.Authorization = `Bearer ${refreshedUser.access_token}`;
          return $axios(originalRequest);
        } catch (err) {
          return Promise.reject(error);
        }
      }

      return Promise.reject(error);
    }

    return Promise.reject(error);
  },
);

// Helper function to restore URL after authentication/logout cycle
const checkAndRestoreReturnUrl = () => {
  // This runs when the app loads to check if we need to redirect after auth
  const urlParams = new URLSearchParams(window.location.search);
  const postLogoutReturnUrl = localStorage.getItem('post_logout_return_url');

  // Check if we're on the home page after logout and have a saved URL
  if (postLogoutReturnUrl && window.location.pathname === '/') {
    console.log(
      'Checking if we should restore post-logout URL:',
      postLogoutReturnUrl,
    );
    // We'll let the Navigation component handle this after getUser() completes
  }
};

// Check for return URLs when app loads
checkAndRestoreReturnUrl();

/* eslint-disable no-new */
new Vue({
  el: '#app',
  router,
  store,
  components: { App },
  template: '<App/>',
});



// WEBPACK FOOTER //
// ./src/main.js